import { forwardRef, ReactNode, SVGProps, useImperativeHandle, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { OnClickFunctionType } from '@/types/common-dom/types';
import { CaratDown } from '@weee-fe/kunlunicon';
import Link from 'next/link';

type ComponentProps = {
  label: string;
  onClick?: OnClickFunctionType;
  link?: string;
  mainColorTwNotation?: string /* Example: text-surface-100-fg-minor PS: string interpolation won't work */;
  mainIcon?:
    | SVGProps<SVGSVGElement>
    | ReactNode /* Example, on the parent of this Dummy UI component, you import your "kunlunicon" */;
  wrapperClassName?: string;
  isSelected?: boolean;
  showCarat?: boolean;
  flipCarat?: boolean;
  dataTestId?: string;
};

const NavTab = (props: ComponentProps) => {
  const {
    label,
    link,
    onClick,
    mainColorTwNotation = 'text-surface-100-fg-minor',
    wrapperClassName = '',
    isSelected,
    showCarat = false,
    flipCarat = false,
    dataTestId
  } = props;

  const BtnLabel = link ? 'div' : 'button';

  const renderBtn = (
    <BtnLabel
      className={twMerge(
        'relative mx-1 whitespace-nowrap',
        BtnLabel === 'button' && 'border-0 text-inherit outline-none bg-transparent cursor-pointer appearance-none',
        wrapperClassName || ''
      )}
      data-testid={dataTestId}
      type={BtnLabel === 'button' ? 'button' : undefined}
      onClick={props?.onClick || null}
      aria-label={label}>
      <div className={twMerge('flex items-center gap-200 z-[2]')}>
        {props?.mainIcon && <div className="flex items-center h-full">{props?.mainIcon}</div>}
        <div
          className={twMerge(
            'flex h-[30px] items-center font-normal first-letter:uppercase',
            mainColorTwNotation,
            isSelected && 'text-primary-1 font-semibold'
          )}>
          {label}
          {showCarat && (
            <span className="inline-flex h-full px-50">
              <CaratDown
                width={12}
                height={12}
                className={twMerge(
                  flipCarat ? 'rotate-180 pb-[1px]' : 'pt-[1px]',
                  'z-[2]',
                  'text-surface-100-fg-minor'
                )}
              />
            </span>
          )}
        </div>
      </div>
      {isSelected && <div className="absolute bottom-0 left-0 right-0 w-full h-[2px] bg-btn-secondary-bg" />}
    </BtnLabel>
  );

  if (link) {
    return (
      <Link prefetch={false} href={link}>
        <a aria-label={label} className="flex" onClick={onClick}>
          {renderBtn}
        </a>
      </Link>
    );
  }
  return renderBtn;
};

export const ForwardRefableNavTab = forwardRef((props: any, ref) => {
  const containerRef = useRef(null);

  useImperativeHandle(ref, () => {
    return {
      containerRef
    };
  });

  return (
    <span ref={containerRef}>
      <NavTab {...props} />
    </span>
  );
});

export default NavTab;
